import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { Service } from '@app/services/_models/service.model';
import { TranslateService } from '@ngx-translate/core';
import { NomenclatureService } from '@services/nomenclature.service';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { PromoCampaignConfigService } from '../_services/promo-campaign-config.service';
import { PromoCampaignConfig } from '../_models/promo-campaign-config.model';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-edit-promo-campaign',
  templateUrl: './add-edit-promo-campaign.component.html',
  styleUrls: ['./add-edit-promo-campaign.component.css']
})
export class AddEditPromoCampaignComponent extends BaseParentComponent implements OnInit {

  additionalServices: Service[] = [];
  disableActionBtn = false;
  dataLoaded = false;

  mainForm = this.fb.group({
    id:                 [null],
    service:            [null, Validators.required],
    hasPromoPrice:      [false],
    discountValue:      [null],
    discountText:       [null],
    discountKind:       [null],
    discountTextColor:  [null],
    hasTrigger:         [false],
    triggerServices:    this.fb.array([]),
    isActive:           [true],
    versionData:        [null]
  });

  loadData$ = this.route.queryParams.pipe(
    concatMap(params => {
      return forkJoin([
        params['id']
          ? this.promoCampaignConfigService.findById(+params['id'])
          : of(null),
        this.nomenclatureService.findAllAdditionalActiveServices()
      ]).pipe(
        tap(([config, services]: [PromoCampaignConfig | null, Service[]]) => {
          this.additionalServices = services ?? [];

          if (config) {
            this.mainForm.patchValue({
              id: config.id,
              service: config.service,
              hasPromoPrice: config.hasPromoPrice,
              discountValue: config.discountValue,
              discountText: config.discountText,
              discountKind: config.discountKind,
              discountTextColor: config.discountTextColor,
              hasTrigger: config.hasTrigger,
              isActive: config.isActive,
              versionData: config.versionData
            });

            if (config.hasPromoPrice) {
              this.addPromoPriceValidators();
            }

            if (config.hasTrigger && config.triggerServices?.length) {
              config.triggerServices.forEach(s => this.addTriggerService(s));
            }
          }

          this.dataLoaded = true;
        }),
        catchError(err => {
          displayError(err);
          this.dataLoaded = false;
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          return EMPTY;
        })
      );
    })
  );

  constructor(
    private router:                       Router,
    private route:                        ActivatedRoute,
    private fb:                           FormBuilder,
    private uiEvent:                      UIEventCustom,
    private toastr:                       ToastrService,
    private translateService:             TranslateService,
    private promoCampaignConfigService:   PromoCampaignConfigService,
    private nomenclatureService:          NomenclatureService,
  ) {
    super(router);
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data
    });
  }

  get triggerServicesArray(): FormArray {
    return this.mainForm.get('triggerServices') as FormArray;
  }

  compareByMobileAppId(a: Service, b: Service): boolean {
    return !!a && !!b && a.mobileAppId === b.mobileAppId;
  }

  get f() {
    return this.mainForm.controls;
  }

  onHasPromoPriceChange(event: any) {
    if (event.target.checked) {
      this.addPromoPriceValidators();
    } else {
      this.clearPromoPriceValidators();
    }
  }

  private addPromoPriceValidators() {
    this.mainForm.get('discountValue')?.setValidators([Validators.required, Validators.min(0)]);
    this.mainForm.get('discountKind')?.setValidators([Validators.required]);
    this.mainForm.get('discountValue')?.updateValueAndValidity();
    this.mainForm.get('discountKind')?.updateValueAndValidity();
  }

  private clearPromoPriceValidators() {
    this.mainForm.get('discountValue')?.clearValidators();
    this.mainForm.get('discountKind')?.clearValidators();
    this.mainForm.get('discountValue')?.updateValueAndValidity();
    this.mainForm.get('discountKind')?.updateValueAndValidity();
  }

  addTriggerService(service?: Service) {
    this.triggerServicesArray.push(this.fb.group({
      mobileAppId: [service?.mobileAppId ?? null, Validators.required],
      name:        [service?.name ?? null]
    }));
  }

  removeTriggerService(index: number) {
    this.triggerServicesArray.removeAt(index);
  }

  onTriggerServiceChange(event: any, index: number) {
    const selectedMobileAppId = +event.target.value;
    const service = this.additionalServices.find(s => s.mobileAppId === selectedMobileAppId);
    this.triggerServicesArray.at(index).patchValue({
      name: service?.name ?? null
    });
  }

  save() {
    if (!this.mainForm.valid) {
      this.uiEvent.displayUIError();
      this.showValidationsSubject.next(true);
      return;
    }

    const formValue = this.mainForm.value;
    const config: PromoCampaignConfig = {
      id:               formValue.id,
      service:          formValue.service,
      hasPromoPrice:    formValue.hasPromoPrice,
      discountValue:    formValue.hasPromoPrice ? formValue.discountValue : null,
      discountText:     formValue.hasPromoPrice ? formValue.discountText : null,
      discountKind:     formValue.hasPromoPrice ? formValue.discountKind : null,
      discountTextColor: formValue.hasPromoPrice ? formValue.discountTextColor : null,
      hasTrigger:       formValue.hasTrigger,
      triggerServices:  formValue.hasTrigger
        ? (formValue.triggerServices as any[]).map(ts => ({ mobileAppId: ts.mobileAppId, name: ts.name } as Service))
        : [],
      isActive:         formValue.isActive,
      versionData:      formValue.versionData
    };

    this.disableActionBtn = true;
    this.promoCampaignConfigService.save(config).subscribe({
      next: () => {
        this.uiEvent.displayUISuccess();
        this.disableActionBtn = false;
        this.router.navigate(['/list-promo-campaign-configs']);
      },
      error: err => {
        displayError(err);
        this.disableActionBtn = false;
        const apiCode = err?.error?.code;
        const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
        const translated = i18nKey ? this.translateService.instant(i18nKey) : null;
        translated && translated !== i18nKey
          ? this.toastr.error(translated, this.translateService.instant('toast.errorTitle'))
          : this.uiEvent.displayUIError();
      }
    });
  }
}
